import { useMemo } from 'react';
import { useSmartTagAudiencesForPickerQuery } from './SmartTagAudiences.graphql.generated';
import type { RsvpSmartAudienceStatus, SmartAudienceConfig, SmartAudienceOption } from './smartAudience.types';
import { buildRsvpStateRule, rsvpStatusOfRule, smartAudienceIdFor } from './smartAudience.utils';

type UseSmartAudiencesArgs = Readonly<{
  eventId: string | undefined;
  config: SmartAudienceConfig | undefined;
}>;

type UseSmartAudiencesResult = Readonly<{
  smartAudiences: ReadonlyArray<SmartAudienceOption>;
  /**
   * True until the first answer, from the cache or the network. A count refresh doesn't set it, so the
   * picker stays usable while counts move.
   */
  loading: boolean;
}>;

const EMPTY: ReadonlyArray<SmartAudienceOption> = [];

/**
 * The smart tags this picker offers, in the caller's order.
 *
 * The server owns which audiences exist for an event: `smartTagAudiences` returns an empty list for an
 * event outside the `smartTags` rollout, so the picker needs no flag of its own. A failed fetch reads
 * the same way — the picker falls back to tags and guests rather than blocking the compose.
 */
export const useSmartAudiences = ({ eventId, config }: UseSmartAudiencesArgs): UseSmartAudiencesResult => {
  // Keyed by value, so a caller passing an inline `{ statuses: [...] }` doesn't rebuild the options every render.
  const statusesKey = config?.statuses.join(',') ?? '';
  const enabled = !!eventId && statusesKey.length > 0;
  const { data, loading } = useSmartTagAudiencesForPickerQuery({
    variables: { eventId: eventId ?? '' },
    skip: !enabled,
    // Counts move as guests RSVP; the cache answers instantly while the network refreshes them.
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast',
  });

  const smartAudiences = useMemo(() => {
    if (!enabled) return EMPTY;
    const offered = data?.smartTagAudiences ?? [];
    return (statusesKey.split(',') as ReadonlyArray<RsvpSmartAudienceStatus>).flatMap(status => {
      const option = offered.find(candidate => rsvpStatusOfRule(candidate) === status);
      if (!option) return [];
      return [{ id: smartAudienceIdFor(status), status, rule: buildRsvpStateRule(status), memberCount: option.capped ? null : option.memberCount, capped: option.capped }];
    });
  }, [statusesKey, data, enabled]);

  // A failed fetch has settled too: the picker opens without smart tags rather than staying shut.
  return { smartAudiences, loading: enabled && loading && !data };
};
